import React from 'react';
import { css } from '@emotion/react';
import { BirdiTheme } from '@birdi/theme';

/**
 * A tiny, dependency-free Markdown renderer for the assistant's replies.
 * Covers the subset the agent actually emits: bold, italic, inline code,
 * links, bullet/numbered lists, headings, and paragraphs.
 *
 * It builds React elements (never dangerouslySetInnerHTML), so all text is
 * escaped by React and there is no XSS surface. Incomplete markdown mid-stream
 * simply renders as literal text until the closing token arrives.
 */

// Inline: code | [text](url) | **bold** | *italic* / _italic_ | bare URL.
// Order matters — code first so ** inside code isn't bolded; bold before
// italic; markdown links before bare URLs so the URL-in-parens isn't
// double-matched.
const INLINE =
  /(`[^`]+`)|(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(_[^_\n]+_)|(https?:\/\/[^\s<>()]+)/g;

/**
 * Hosts the assistant may render as a clickable link. Subdomains included, so
 * `help.birdi.io` is covered without listing it.
 *
 * Deliberately short. The agent only ever hands out help-centre articles and
 * in-app paths; `report_bug` is explicitly told never to paste a tracker link,
 * and the CDN/Outline hosts are internal. Anything else is either a mistake or
 * something that reached the model through content it read — a map name, an
 * annotation label, a help article — and a link the user only has to click is
 * the wrong place to find out which.
 */
const LINK_ALLOWED_HOSTS = ['birdi.io'];

/**
 * The href to render, or null to fall back to plain text.
 *
 * Blocking scheme (`javascript:`, `data:`) stops XSS; blocking HOST stops the
 * model being used to place a link. A blocked URL still shows as text, so
 * nothing is hidden from the user — it just isn't one click away.
 */
export const safeHref = (url: string): string | null => {
  const u = url.trim();
  // Same-origin app path. `//evil.com` also starts with a slash and resolves
  // to a foreign origin, so it has to be excluded explicitly.
  if (u.startsWith('/')) return u.startsWith('//') ? null : u;

  let parsed: URL;
  try {
    parsed = new URL(u);
  } catch {
    return null;
  }
  // Parse rather than pattern-match: `https://birdi.io@evil.com` and
  // `https://evil.com/?ref=birdi.io` both contain the string and neither is
  // ours. Only `hostname` is trustworthy here.
  if (parsed.protocol !== 'https:') return null;
  const host = parsed.hostname.toLowerCase();
  const allowed = LINK_ALLOWED_HOSTS.some(
    (h) => host === h || host.endsWith(`.${h}`),
  );
  return allowed ? u : null;
};

function parseInline(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  INLINE.lastIndex = 0;
  // eslint-disable-next-line no-cond-assign
  while ((m = INLINE.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyBase}-${i++}`;
    if (m[1]) {
      out.push(<code key={key}>{m[1].slice(1, -1)}</code>);
    } else if (m[2]) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(m[2]);
      const href = linkMatch ? safeHref(linkMatch[2]) : null;
      if (linkMatch && href) {
        out.push(
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {linkMatch[1]}
          </a>,
        );
      } else {
        out.push(m[2]);
      }
    } else if (m[3]) {
      out.push(<strong key={key}>{m[3].slice(2, -2)}</strong>);
    } else if (m[4]) {
      out.push(<em key={key}>{m[4].slice(1, -1)}</em>);
    } else if (m[5]) {
      out.push(<em key={key}>{m[5].slice(1, -1)}</em>);
    } else if (m[6]) {
      // Bare URL — autolink, leaving trailing sentence punctuation outside.
      const trail = /[.,;:!?]+$/.exec(m[6])?.[0] ?? '';
      const url = trail ? m[6].slice(0, -trail.length) : m[6];
      const href = safeHref(url);
      if (href) {
        out.push(
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {url}
          </a>,
        );
        if (trail) out.push(trail);
      } else {
        out.push(m[6]);
      }
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'h'; level: number; text: string };

const BULLET = /^\s*[-*]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^(#{1,4})\s+(.*)$/;

function toBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  const flushPara = () => {
    if (para.length) {
      blocks.push({ kind: 'p', lines: para });
      para = [];
    }
  };
  for (const raw of lines) {
    const line = raw;
    if (line.trim() === '') {
      flushPara();
      continue;
    }
    const h = HEADING.exec(line);
    if (h) {
      flushPara();
      blocks.push({ kind: 'h', level: h[1].length, text: h[2] });
      continue;
    }
    const b = BULLET.exec(line);
    if (b) {
      flushPara();
      const prev = blocks[blocks.length - 1];
      if (prev && prev.kind === 'ul') prev.items.push(b[1]);
      else blocks.push({ kind: 'ul', items: [b[1]] });
      continue;
    }
    const n = NUMBERED.exec(line);
    if (n) {
      flushPara();
      const prev = blocks[blocks.length - 1];
      if (prev && prev.kind === 'ol') prev.items.push(n[1]);
      else blocks.push({ kind: 'ol', items: [n[1]] });
      continue;
    }
    para.push(line);
  }
  flushPara();
  return blocks;
}

const mdCss = (theme: BirdiTheme) => css`
  font-size: 0.95em;
  line-height: 1.5;
  word-break: break-word;
  & > *:first-of-type {
    margin-top: 0;
  }
  & > *:last-child {
    margin-bottom: 0;
  }
  p {
    margin: 0 0 0.6em;
  }
  ul,
  ol {
    margin: 0 0 0.6em;
    padding-left: 1.25em;
  }
  li {
    margin: 0.15em 0;
  }
  h3,
  h4,
  h5 {
    margin: 0.4em 0 0.3em;
    font-weight: 700;
    line-height: 1.3;
  }
  h3 {
    font-size: 1.05em;
  }
  h4,
  h5 {
    font-size: 1em;
  }
  strong {
    font-weight: 700;
  }
  code {
    font-family: "SFMono-Regular", ui-monospace, Menlo, Consolas, monospace;
    font-size: 0.86em;
    background: ${theme.mono0};
    border: 1px solid ${theme.mono20};
    border-radius: 4px;
    padding: 0.05em 0.35em;
  }
  a {
    color: ${theme.ocean};
    text-decoration: underline;
  }
`;

export const Markdown: React.FC<{ content: string }> = ({ content }) => {
  const blocks = toBlocks(content);
  return (
    <div css={mdCss}>
      {blocks.map((block, i) => {
        if (block.kind === 'h') {
          const Tag = (block.level <= 1 ? 'h3' : block.level === 2 ? 'h4' : 'h5') as
            | 'h3'
            | 'h4'
            | 'h5';
          return <Tag key={i}>{parseInline(block.text, `h${i}`)}</Tag>;
        }
        if (block.kind === 'ul') {
          return (
            <ul key={i}>
              {block.items.map((it, j) => (
                <li key={j}>{parseInline(it, `ul${i}-${j}`)}</li>
              ))}
            </ul>
          );
        }
        if (block.kind === 'ol') {
          return (
            <ol key={i}>
              {block.items.map((it, j) => (
                <li key={j}>{parseInline(it, `ol${i}-${j}`)}</li>
              ))}
            </ol>
          );
        }
        // paragraph — single newlines become <br/>
        return (
          <p key={i}>
            {block.lines.map((ln, j) => (
              <React.Fragment key={j}>
                {j > 0 && <br />}
                {parseInline(ln, `p${i}-${j}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};
